import * as z from 'zod';
import { nanoid } from 'nanoid';

import {
  genderIds,
  maxAge,
  minAge,
  patternFcpId,
  programCountryIsoCodes,
} from '@sola/contentful-model/lib/sac-values.ts';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import { LOCALES_CODES } from '@sola/contentful-model/lib/types.ts';

import { DisplayVariant } from './displays.ts';
import { IconNamesSafe } from './icons/icon-names.ts';
import { LOGO_NAMES } from './logos.ts';
import {
  AssemblySchema,
  BadgeColors,
  ContentfulEntryReference,
  ContentfulLinkReference,
  EmbedProps,
  FormDynamicSettings,
  TemplateContentGlobal,
} from './schema.ts';

export const AppSyncTarget = z.enum(['cp', 'rd']);
export type AppSyncTarget = z.infer<typeof AppSyncTarget>;

/** Semantic aliases for AppSyncTarget values */
export const AppSyncTargetKey = {
  Campaigns: 'cp',
  Redirects: 'rd',
} as const satisfies Record<string, AppSyncTarget>;

export const LinkType = z.enum([
  'internal',
  'external',
  'anchor',
  'mailto',
  'download',
  'tel',
  'none',
]);
export type LinkType = z.infer<typeof LinkType>;

const JsonLinkEmpty = z.object({
  type: z.literal(LinkType.enum.none),
});
const JsonLinkInternal = z.object({
  type: z.literal(LinkType.enum.internal),
  title: z.string(),
  entry: ContentfulLinkReference,
  parameters: z.string().optional(),
  anchor: z.string().optional(),
  icon: z.string().optional(),
  iconPlacement: z.enum(['before', 'after']).optional(),
});
const JsonLinkExternal = z.object({
  type: z.literal(LinkType.enum.external),
  title: z.string(),
  externalUrl: z
    .string({
      error: (issue) =>
        issue.input === undefined ? 'Please enter a url.' : 'Not a string',
    })
    .url('Please provide a valid url.'),
  icon: z.string().optional(),
  iconPlacement: z.enum(['before', 'after']).optional(),
});
const JsonLinkAnchor = z.object({
  type: z.literal(LinkType.enum.anchor),
  title: z.string(),
  anchor: z.string(),
  icon: z.string().optional(),
  iconPlacement: z.enum(['before', 'after']).optional(),
});
const JsonLinkMailto = z.object({
  type: z.literal(LinkType.enum.mailto),
  title: z.string(),
  mailto: z.string(),
  icon: z.string().optional(),
  iconPlacement: z.enum(['before', 'after']).optional(),
});
const JsonLinkTel = z.object({
  type: z.literal(LinkType.enum.tel),
  title: z.string(),
  tel: z.string(),
  icon: z.string().optional(),
  iconPlacement: z.enum(['before', 'after']).optional(),
});
const JsonLinkDownload = z.object({
  type: z.literal(LinkType.enum.download),
  title: z.string(),
  download: ContentfulLinkReference,
  icon: z.string().optional(),
  iconPlacement: z.enum(['before', 'after']).optional(),
});

export const JsonLinkEntry = z.union([
  JsonLinkInternal.partial(),
  JsonLinkDownload.partial(),
]);
export type JsonLinkEntry = z.infer<typeof JsonLinkEntry>;

export const JsonLink = z.discriminatedUnion('type', [
  JsonLinkInternal,
  JsonLinkExternal,
  JsonLinkAnchor,
  JsonLinkMailto,
  JsonLinkTel,
  JsonLinkDownload,
  JsonLinkEmpty,
]);
export type JsonLink = z.infer<typeof JsonLink>;

export const JsonLinkPartial = z.union([
  JsonLinkInternal.partial(),
  JsonLinkExternal.partial(),
  JsonLinkAnchor.partial(),
  JsonLinkEmpty.partial(),
  JsonLinkDownload.partial(),
]);
export type JsonLinkPartial = z.infer<typeof JsonLinkPartial>;

export const JsonFieldType = z.enum(['quotation', 'badge', 'video', 'none']);
export type JsonFieldType = z.infer<typeof JsonFieldType>;

export const JsonFieldQuotation = z.object({
  text: z.string().optional(),
  attribution: z.string().optional(),
  title: z.string().optional(),
});
export type JsonFieldQuotation = z.infer<typeof JsonFieldQuotation>;

export const JsonFieldLogo = z.object({
  // eslint-disable-next-line unicorn/no-useless-undefined -- Zod .catch() requires an explicit argument
  logo: LOGO_NAMES.optional().catch(undefined),
  alt: z.string().optional(),
});
export type JsonFieldLogo = z.infer<typeof JsonFieldLogo>;

export const JsonFieldBadge = z.object({
  text: z.string().optional(),
  color: BadgeColors.optional(),
});
export type JsonFieldBadge = z.infer<typeof JsonFieldBadge>;

export const JsonFieldEmbedSettings = z.object({
  url: z.string().optional(),
});
export type JsonFieldEmbedSettings = z.infer<typeof JsonFieldEmbedSettings>;

export const JsonFieldVideo = z.object({
  url: z.string().optional(),
  autoplay: z.boolean().optional(),
  hasProgrammaticButton: z.boolean().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  duration: z.number().optional(),
  thumbnailUrl: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  uploadDate: z.string().optional(),
});
export type JsonFieldVideo = z.infer<typeof JsonFieldVideo>;

export const FundSettingsOption = z.object({
  key: z.string().default(() => nanoid()),
  value: z.coerce.number().optional(),
  label: z.string().optional(),
  badge: z.string().optional(),
  impact: z.string().optional(),
  isDefault: z.boolean().optional(),
});
export type FundSettingsOption = z.infer<typeof FundSettingsOption>;

export const JsonFieldFundSettings = z.object({
  entry: ContentfulEntryReference.optional().nullable(),
  suggestedAmount: z.coerce.number().optional().nullable(),
  suggestedAmountsOneTime: FundSettingsOption.array()
    .max(5)
    .optional()
    .nullable(),
  suggestedAmountsMonthly: FundSettingsOption.array()
    .max(5)
    .optional()
    .nullable(),
  suggestedAmountsAnnually: FundSettingsOption.array()
    .max(5)
    .optional()
    .nullable(),
  suggestedDonateFrequency: z.string().optional(),
});
export type JsonFieldFundSettings = z.infer<typeof JsonFieldFundSettings>;

export const JsonField = z.union([
  JsonFieldQuotation,
  JsonFieldBadge,
  JsonFieldEmbedSettings,
  JsonFieldFundSettings,
  JsonFieldVideo,
]);
export type JsonField = z.infer<typeof JsonField>;

export const JsonFieldPartial = z.union([
  JsonFieldQuotation.partial(),
  JsonFieldBadge.partial(),
  JsonFieldEmbedSettings.partial(),
  JsonFieldFundSettings.partial(),
  JsonFieldVideo.partial(),
]);
export type JsonFieldPartial = z.infer<typeof JsonFieldPartial>;

export const JsonFieldFormSettings = FormDynamicSettings.passthrough();
export type JsonFieldFormSettings = z.infer<typeof JsonFieldFormSettings>;

const PageLink = z.object({
  sys: z.object({
    id: z.string(),
  }),
  path: z.string().optional().nullable(),
});
const ArticleLink = z.object({
  sys: z.object({
    id: z.string(),
  }),
  slug: z.string().optional().nullable(),
  taxonomyArticleType: z.string().optional().nullable(),
});

const DownloadLink = z.object({
  sys: z.object({ id: z.string() }),
  file: z
    .object({
      url: z.string().optional().nullable(),
      title: z.string().optional().nullable(),
      contentType: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
});

export type ArticleLinkResponseDto = z.infer<typeof ArticleLink>;

export const LinkedContentResponseDto = z
  .object({
    items: z
      .object({
        __typename: z.string(),
        ...PageLink.shape,
        ...ArticleLink.shape,
        ...DownloadLink.shape,
      })
      .array()
      .optional()
      .nullable(),
  })
  .optional()
  .nullable();
export type LinkedContentResponseDto = z.infer<typeof LinkedContentResponseDto>;

export const FundResponseDto = z.object({
  __typename: z.literal('Fund'),
  sys: z.object({ id: z.string() }),
  tcmId: z.string().optional().nullable(),
  fundTitle: z.string().optional().nullable(),
  fundCodeOneTime: z.string().optional().nullable(),
  fundCodeCommitment: z.string().optional().nullable(),
  interventionId: z.string().optional().nullable(),
  icon: IconNamesSafe.optional().nullable(),
  minOneTimeAmount: z.coerce.number().optional().nullable(),
  donationLimitTotal: z.coerce.number().optional().nullable(),
  availableFrequencies: z.string().array().optional().nullable(),
  taxonomyCmsOrganization: z.string().array().optional().nullable(),
});
export type FundResponseDto = z.infer<typeof FundResponseDto>;

export const ReferencedContentResponseDto = z
  .object({
    items: z.custom<FundResponseDto>().array().optional().nullable(),
  })
  .optional()
  .nullable();
export type ReferencedContentResponseDto = z.infer<
  typeof ReferencedContentResponseDto
>;

const Media = z.object({
  image: z
    .object({ url: z.string(), height: z.number(), width: z.number() })
    .optional()
    .nullable(),
  altText: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  imageFocalPoint: z
    .object({
      focalPoint: z
        .object({
          x: z.string(),
          y: z.string(),
        })
        .optional()
        .nullable(),
      ratio: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  video: JsonFieldVideo.optional().nullable(),
});
export type Media = z.infer<typeof Media>;

const EmbedContent = z.object({
  __typename: z.string(),
  sys: z.object({
    id: z.string(),
  }),
  title: z.string(),
});
export type EmbedContent = z.infer<typeof EmbedContent>;

const EmbedMedia = Media.extend({
  __typename: z.string(),
  sys: z.object({
    id: z.string(),
  }),
});
export type EmbedMedia = z.infer<typeof EmbedMedia>;

export const ContentResponseBody = z.object({
  json: z.object({}).passthrough().optional().nullable(),
  links: z
    .object({
      entries: z.object({
        hyperlink: z
          .object({
            __typename: z.string(),
            ...PageLink.shape,
            ...ArticleLink.shape,
            ...DownloadLink.shape,
          })
          .nullable()
          .array()
          .optional()
          .nullable(),
        block: z.custom<EmbedContent | EmbedMedia>().optional().nullable(),
      }),
    })
    .optional()
    .nullable(),
});
export type ContentResponseBody = z.infer<typeof ContentResponseBody>;

export const ContentResponseDtoIndividual = z.object({
  __typename: z.literal('FlexibleContent'), // @TODO keys.contentTypes.flexibleContent
  sys: z.object({
    id: z.string(),
    publishedAt: z.string().optional().nullable(),
  }),
  title: z.string().optional().nullable(),
  subtitle: z.string().optional().nullable(),
  body: ContentResponseBody.optional().nullable(),
  icon: IconNamesSafe.optional().nullable(),
  badge: JsonFieldBadge.optional().nullable(),
  quotation: JsonFieldQuotation.optional().nullable(),
  logo: JsonFieldLogo.optional().nullable(),
  primaryCTA: JsonLink.optional().nullable(),
  secondaryCTA: JsonLink.optional().nullable(),
  tertiaryCTA: JsonLink.optional().nullable(),
  // @TODO - contact contentful about live preview bug, loads graphql with camelcase and liveupdate with fieldcase
  primaryCta: JsonLink.optional().nullable(),
  secondaryCta: JsonLink.optional().nullable(),
  tertiaryCta: JsonLink.optional().nullable(),
  media: Media.optional().nullable(),
  mediaLogo: Media.optional().nullable(),
  displayDate: z.string().optional().nullable(),
  fund: JsonFieldFundSettings.optional().nullable(),
  linkedContentCollection: LinkedContentResponseDto,
  referencedContentCollection: ReferencedContentResponseDto,
});
export type ContentResponseDtoIndividual = z.infer<
  typeof ContentResponseDtoIndividual
>;

export const ContentResponseDto = ContentResponseDtoIndividual.extend({
  itemsCollection: z
    .object({
      items: ContentResponseDtoIndividual.array().optional().nullable(),
    })
    .optional()
    .nullable(),
});
export type ContentResponseDto = z.infer<typeof ContentResponseDto>;

export const ContentResponseIndexDto = ContentResponseDto.extend({
  adminTitle: z.string().optional().nullable(),
  contentPosition: z.string().array().optional().nullable(),
  linkedFrom: z
    .object({
      entryCollection: z
        .object({
          total: z.number(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  taxonomyCmsOrganization: z.string().optional().nullable(),
});
export type ContentResponseIndexDto = z.infer<typeof ContentResponseIndexDto>;

export const LongformContentResponseDto = z.object({
  __typename: z.literal('LongformContent'), // @TODO keys.contentTypes.longformContent
  sys: z.object({
    id: z.string(),
    publishedAt: z.string().optional().nullable(),
  }),
  body: ContentResponseBody.optional().nullable(),
});
export type LongformContentResponseDto = z.infer<
  typeof LongformContentResponseDto
>;

export const LongformContentResponseIndexDto =
  LongformContentResponseDto.extend({
    adminTitle: z.string().optional().nullable(),
    contentPosition: z.string().array().optional().nullable(),
    linkedFrom: z
      .object({
        entryCollection: z
          .object({
            total: z.number(),
          })
          .optional()
          .nullable(),
      })
      .optional()
      .nullable(),
    taxonomyCmsOrganization: z.string().optional().nullable(),
  });
export type LongformContentResponseIndexDto = z.infer<
  typeof LongformContentResponseIndexDto
>;

export const FormConfigResponseDto = z.object({
  __typename: z.literal('FormConfig'),
  sys: z.object({
    id: z.string(),
    publishedAt: z.string().optional().nullable(),
  }),
  config: JsonFieldFormSettings.optional().nullable(),
  relatedContent: ContentResponseDtoIndividual.optional().nullable(),
  linkedContentCollection: LinkedContentResponseDto,
});
export type FormConfigResponseDto = z.infer<typeof FormConfigResponseDto>;

export const FormConfigResponseIndexDto = FormConfigResponseDto.extend({
  adminTitle: z.string().optional().nullable(),
  contentPosition: z.string().array().optional().nullable(),
  linkedFrom: z
    .object({
      entryCollection: z
        .object({
          total: z.number(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  taxonomyCmsOrganization: z.string().optional().nullable(),
});
export type FormConfigResponseIndexDto = z.infer<
  typeof FormConfigResponseIndexDto
>;

export const SacFilterResponseDto = z.object({
  __typename: z.literal('SacFilter'),
  sys: z.object({ id: z.string() }),
  limit: z.number().int().positive().lt(1000).default(12).optional().nullable(),
  ageMax: z.number().min(minAge).max(maxAge).optional().nullable(),
  ageMin: z.number().min(minAge).max(maxAge).optional().nullable(),
  birthDate: z.string().datetime().optional().nullable(),
  centerId: z.string().regex(patternFcpId).optional().nullable(),
  countries: z.enum(programCountryIsoCodes).array().optional().nullable(),
  gender: z.enum(genderIds).optional().nullable(),
  disability: z.boolean().optional().nullable(),
  hiv: z.boolean().optional().nullable(),
  highlyVulnerable: z.boolean().optional().nullable(),
  longestWaiting: z.boolean().optional().nullable(),
  orphan: z.boolean().optional().nullable(),
  relatedContent: ContentResponseDtoIndividual.optional().nullable(),
});
export type SacFilterResponseDto = z.infer<typeof SacFilterResponseDto>;

export const MenuItemResponseDto = z.object({
  __typename: z.literal('Navigation'),
  sys: z.object({ id: z.string() }),
  link: JsonLink.optional().nullable(),
  title: z.string().optional().nullable(),
  summary: z.string().optional().nullable(),
  icon: IconNamesSafe.optional().nullable(),
  badge: JsonFieldBadge.optional().nullable(),
  contentPosition: z.string().array().optional().nullable(),
  displayVariant: DisplayVariant.optional().nullable(),
  cta: z
    .custom<ContentResponseDtoIndividual | SacFilterResponseDto>()
    .optional()
    .nullable(),
  linkedContentCollection: LinkedContentResponseDto,
  featuredArticles: z.boolean().optional().nullable(),
});

export const NavResponseSimpleDto = z.object({
  __typename: z.literal('Navigation'),
  sys: z.object({ id: z.string() }),
});
export type NavResponseSimpleDto = z.infer<typeof NavResponseSimpleDto>;

export const NavResponseDto = MenuItemResponseDto.extend({
  __typename: z.literal('Navigation'), // @TODO keys.contentTypes.navigation
  subNavCollection: z
    .object({
      items: MenuItemResponseDto.extend({
        subNavCollection: z
          .object({
            items: MenuItemResponseDto.extend({
              subNavCollection: z
                .object({
                  items: MenuItemResponseDto.array().optional().nullable(),
                })
                .optional()
                .nullable(),
            })
              .array()
              .optional()
              .nullable(),
          })
          .optional()
          .nullable(),
      })
        .array()
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
});
export type NavResponseDto = z.infer<typeof NavResponseDto>;

export const EmbedResponseDto = z.object({
  __typename: z.literal('Embed'),
  sys: z.object({ id: z.string() }),
  embedSettings: EmbedProps.optional().nullable(),
});
export type EmbedResponseDto = z.infer<typeof EmbedResponseDto>;

export const ContributorResponseDto = z.object({
  __typename: z.literal('Contributor'),
  sys: z.object({ id: z.string() }),
  name: z.string().optional().nullable(),
  position: z.string().optional().nullable(),
  image: z
    .object({ url: z.string(), height: z.number(), width: z.number() })
    .optional()
    .nullable(),
});
export type ContributorResponseDto = z.infer<typeof ContributorResponseDto>;

export const PartnerResponseDto = z.object({
  __typename: z.literal('PartnerCoBranding'),
  sys: z.object({
    id: z.string(),
    publishedAt: z.string().optional().nullable(),
  }),
  adminTitle: z.string().optional().nullable(),
  name: z.string(),
  slug: z.string(),
  sourceCode: z.string().optional().nullable(),
  shortDescription: ContentResponseBody.optional().nullable(),
  logo: z
    .object({ url: z.string(), height: z.number(), width: z.number() })
    .optional()
    .nullable(),
  bannerImage: z
    .object({ url: z.string(), height: z.number(), width: z.number() })
    .optional()
    .nullable(),
  feature: z.custom<ContentResponseDtoIndividual>().optional().nullable(),
});

export type PartnerResponseDto = z.infer<typeof PartnerResponseDto>;

export const PageResponseItemDto = z.discriminatedUnion('__typename', [
  ContentResponseDto,
  FormConfigResponseDto,
  LongformContentResponseDto,
  FundResponseDto,
  NavResponseDto,
  SacFilterResponseDto,
  EmbedResponseDto,
  PartnerResponseDto,
]);
export type PageResponseItemDto = z.infer<typeof PageResponseItemDto>;

export type FlexibleContentDto = Extract<
  PageResponseItemDto,
  { __typename: 'FlexibleContent' }
>;

export const PageHierarchyFieldValue = z.object({
  ancestors: z
    .object({
      path: z.string(),
      pageTitle: z.partialRecord(LOCALES_CODES, z.string()),
    })
    .array(),
});
export type PageHierarchyFieldValue = z.infer<typeof PageHierarchyFieldValue>;

export const EntryResponseDto = z.object({
  __typename: z.string(),
  sys: z.object({
    id: z.string(),
    publishedAt: z.string().optional().nullable(),
  }),
});
export type EntryResponseDto = z.infer<typeof EntryResponseDto>;

// @See: graphqlFieldsEntryPaths
export const PathResponseDto = z.object({
  __typename: z.string(),
  sys: z.object({
    id: z.string(),
    publishedAt: z.string().optional().nullable(),
    publishedVersion: z.number().optional().nullable(),
  }),
  slug: z.string().optional().nullable(),
  path: z.string().optional().nullable(),
  noIndexSeo: z.boolean().optional().nullable(),
  languagesToPublish: LOCALES_CODES.array().optional().nullable(),
  taxonomyArticleType: ArticleTypesEnum.optional().nullable(),
});
export type PathResponseDto = z.infer<typeof PathResponseDto>;

export const PageResponseSimpleDto = EntryResponseDto.extend({
  path: z.string(),
});
export type PageResponseSimpleDto = z.infer<typeof PageResponseSimpleDto>;

export const PageResponseDto = z.object({
  __typename: z.string(),
  sys: z.object({
    id: z.string(),
    publishedAt: z.string().optional().nullable(),
    firstPublishedAt: z.string().optional().nullable(),
  }),
  slug: z.string(),
  path: z.string(),
  pageTitle: z.string().optional().nullable(),
  summary: z.string().optional().nullable(),
  pageHierarchy: PageHierarchyFieldValue.optional().nullable(),
  hierarchyStyle: z.string().optional().nullable(),
  contentPosition: z.string().array().optional().nullable(),
  seoTitle: z.string().optional().nullable(),
  seoMetaDescription: z.string().optional().nullable(),
  attribution: z.string().optional().nullable(),
  shareTitle: z.string().optional().nullable(),
  shareDescription: z.string().optional().nullable(),
  shareImage: z
    .object({
      altText: z.string().optional().nullable(),
      image: z.object({
        url: z.string(),
      }),
      sys: z
        .object({
          id: z.string(),
        })
        .optional(),
    })
    .optional()
    .nullable(),
  noIndexSeo: z.boolean().optional().nullable(),
  noIndexSite: z.boolean().optional().nullable(),
  languagesToPublish: LOCALES_CODES.array().optional().nullable(),
  flexibleContent: AssemblySchema,
  referencedContentCollection: z.object({
    items: PageResponseItemDto.array().optional().nullable(),
  }),
  taxonomyProgramCountry: z.string().array().optional().nullable(),
  taxonomyFunnel: z.string().optional().nullable(),
});
export type PageResponseDto = z.infer<typeof PageResponseDto>;

// @See: graphqlFieldsPageSimple
export const PageSimpleResponseDto = PageResponseDto.pick({
  sys: true,
  path: true,
  noIndexSeo: true,
  contentPosition: true,
  languagesToPublish: true,
});
export type PageSimpleResponseDto = z.infer<typeof PageSimpleResponseDto>;

export const PageResponseIndexDto = PageResponseDto.extend({
  // private index to allow nullable required data
  adminTitle: z.string().optional().nullable(),
  slug: z.string().optional().nullable(),
  path: z.string().optional().nullable(),
  searchKeywords: z.string().optional().nullable(),
  parentPage: z
    .object({
      __typename: z.string().optional().nullable(),
      sys: z.object({
        id: z.string(),
      }),
      slug: z.string(),
      path: z.string(),
    })
    .optional()
    .nullable(),
  legacyRedirectFrom: z.string().optional().nullable(),
  // extend
  template: z.string().optional().nullable(),
  taxonomyFundCategory: z.string().array().optional().nullable(),
  taxonomyFundPromotion: z.string().array().optional().nullable(),
  taxonomyCmsOrganization: z.string().array().optional().nullable(),
  taxonomyContentOwner: z.string().array().optional().nullable(),
  taxonomyPersonalizationSegment: z.string().array().optional().nullable(),
  taxonomyOther: z.string().array().optional().nullable(),
  linkedFrom: z
    .object({
      entryCollection: z
        .object({
          total: z.number(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
});
export type PageResponseIndexDto = z.infer<typeof PageResponseIndexDto>;

export const Item = z.object({
  fields: z.object({
    parentPage: z
      .object({
        sys: z.object({
          id: z.string(),
        }),
      })
      .optional(),
  }),
  metadata: z.array(z.string()).optional().nullable(),
  articleTopics: z.array(z.string()).optional().nullable(),
});

export const IncomingLinksResponse = z.object({
  items: z.array(Item).optional().nullable(),
});

export type IncomingLinksResponse = z.infer<typeof IncomingLinksResponse>;

export const ArticleResponseDto = z.object({
  __typename: z.string(),
  sys: z.object({
    id: z.string(),
    publishedAt: z.string().optional().nullable(),
    firstPublishedAt: z.string().optional().nullable(),
  }),
  slug: z.string(),
  pageTitle: z.string().optional().nullable(),
  summary: z.string().optional().nullable(),
  publishDate: z.string().optional().nullable(),
  primaryMediaCollection: z
    .object({
      items: EmbedMedia.array().optional().nullable(),
    })
    .optional()
    .nullable(),
  intro: ContentResponseBody.optional().nullable(),
  body: ContentResponseBody.optional().nullable(),
  conclusion: ContentResponseBody.optional().nullable(),
  contributorsCollection: z
    .object({
      items: ContributorResponseDto.array().optional().nullable(),
    })
    .optional()
    .nullable(),
  primaryCta: z
    .custom<ContentResponseDtoIndividual | SacFilterResponseDto>()
    .optional()
    .nullable(),
  secondaryCta: z
    .custom<ContentResponseDtoIndividual | SacFilterResponseDto>()
    .optional()
    .nullable(),
  faq: ContentResponseDto.optional().nullable(),
  seoTitle: z.string().optional().nullable(),
  seoMetaDescription: z.string().optional().nullable(),
  attribution: z.string().optional().nullable(),
  shareTitle: z.string().optional().nullable(),
  contentfulEntryID: z.string().optional(),
  shareDescription: z.string().optional().nullable(),
  shareImage: z
    .object({
      altText: z.string().optional().nullable(),
      image: z.object({
        url: z.string(),
      }),
      sys: z
        .object({
          id: z.string(),
        })
        .optional(),
    })
    .optional()
    .nullable(),
  noIndexSeo: z.boolean().optional().nullable(),
  noIndexSite: z.boolean().optional().nullable(),
  languagesToPublish: LOCALES_CODES.array().optional().nullable(),
  taxonomyArticleType: ArticleTypesEnum.optional().nullable(),
  taxonomyArticleTheme: z.string().array().optional().nullable(),
  taxonomyTopic: z.string().array().optional().nullable(),
  taxonomyCmsOrganization: z.string().array().optional().nullable(),
  taxonomyProgramCountry: z.string().array().optional().nullable(),
  taxonomyProgramRegion: z.string().array().optional().nullable(),
  taxonomyFunnel: z.string().optional().nullable(),
});
export type ArticleResponseDto = z.infer<typeof ArticleResponseDto>;

// @See: graphqlFieldsArticleSimple
export const ArticleSimpleResponseDto = ArticleResponseDto.pick({
  sys: true,
  slug: true,
  noIndexSeo: true,
  languagesToPublish: true,
  taxonomyArticleType: true,
  taxonomyTopic: true,
  taxonomyArticleTheme: true,
  taxonomyProgramCountry: true,
});
export type ArticleSimpleResponseDto = z.infer<typeof ArticleSimpleResponseDto>;

export const ArticleResponseIndexDto = ArticleResponseDto.extend({
  // private index to allow nullable required data
  slug: z.string().optional().nullable(),
  searchKeywords: z.string().optional().nullable(),
  parentPage: z
    .object({
      __typename: z.string().optional().nullable(),
      sys: z.object({
        id: z.string(),
      }),
      slug: z.string(),
      path: z.string(),
    })
    .optional()
    .nullable(),
  // extend
  taxonomyContentOwner: z.string().array().optional().nullable(),
  taxonomyPersonalizationSegment: z.string().array().optional().nullable(),
  taxonomyOther: z.string().array().optional().nullable(),
  linkedFrom: z
    .object({
      entryCollection: z
        .object({
          total: z.number(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
});
export type ArticleResponseIndexDto = z.infer<typeof ArticleResponseIndexDto>;

const LocaleValuesSchema = z.record(z.string(), z.unknown());
export const ContentfulCmaFields = z.record(z.string(), LocaleValuesSchema);
export type ContentfulCmaFields = z.infer<typeof ContentfulCmaFields>;

export const ContentfulCmaSys = z.object({
  id: z.string(),
  type: z.string(),
  version: z.number(),
  publishedVersion: z.number().optional(),
  updatedAt: z.string(),
  publishedAt: z.string().optional(),
  firstPublishedAt: z.string().optional(),
  createdAt: z.string(),
});
export type ContentfulCmaSys = z.infer<typeof ContentfulCmaSys>;

export const ContentfulCmaEntry = z.object({
  sys: ContentfulCmaSys,
  fields: ContentfulCmaFields.optional(),
});
export type ContentfulCmaEntry = z.infer<typeof ContentfulCmaEntry>;

export const ContentfulCmaCollection = z.object({
  sys: z.object({ type: z.literal('Array') }),
  total: z.number(),
  skip: z.number(),
  limit: z.number(),
  items: ContentfulCmaEntry.array(),
});
export type ContentfulCmaCollection = z.infer<typeof ContentfulCmaCollection>;

export const SiteConfigHeaderResponseDto = z.object({
  primaryNav: NavResponseSimpleDto,
  userMenu: NavResponseSimpleDto.optional().nullable(),
});
export type SiteConfigHeaderResponseDto = z.infer<
  typeof SiteConfigHeaderResponseDto
>;

export const SiteConfigFooterResponseDto = z.object({
  footerMenuPrimary: NavResponseSimpleDto,
  footerMenuSecondary: NavResponseSimpleDto,
  footerMenuSocialIcons: NavResponseSimpleDto.optional().nullable(),
  footerMenuTrustIcons: NavResponseSimpleDto.optional().nullable(),
});
export type SiteConfigFooterResponseDto = z.infer<
  typeof SiteConfigFooterResponseDto
>;

export const SiteConfigResponseDto = z.object({
  ...SiteConfigHeaderResponseDto.shape,
  ...SiteConfigFooterResponseDto.shape,
  homePage: PageResponseSimpleDto,
  sacListPage: PageResponseSimpleDto,
  childBioPage: PageResponseSimpleDto,
  missingChildPage: PageResponseSimpleDto,
  missingPage: PageResponseSimpleDto,
  searchSidebar: NavResponseSimpleDto.optional().nullable(),
  templateContent: TemplateContentGlobal,
  referencedContentCollection: z.object({
    items: PageResponseItemDto.array().optional().nullable(),
  }),
  partnerCoBranding: PartnerResponseDto.optional().nullable(),
  siteDomain: z.string(),
});
export type SiteConfigResponseDto = z.infer<typeof SiteConfigResponseDto>;

export const SitemapItem = z.object({
  url: z.string().url(),
  lastModified: z.date(),
  alternates: z.object({
    languages: z.partialRecord(LOCALES_CODES, z.string().url()),
  }),
});
export type SitemapItem = z.infer<typeof SitemapItem>;

export const EntryCollection = z.object({
  total: z.number(),
  items: EntryResponseDto.array(),
});
export type EntryCollection = z.infer<typeof EntryCollection>;

export const FlexiblePageCollectionSimple = z.object({
  total: z.number(),
  items: PageSimpleResponseDto.array(),
});
export type FlexiblePageCollectionSimple = z.infer<
  typeof FlexiblePageCollectionSimple
>;

export const ArticleCollectionSimple = z.object({
  total: z.number(),
  items: ArticleSimpleResponseDto.array(),
});
export type ArticleCollectionSimple = z.infer<typeof ArticleCollectionSimple>;

export type TemplateFieldDefinition = {
  id: string;
  legacyPosition: number;
  label: string;
  required?: boolean;
  supportedContent: string[];
};

export type TemplateDefinition = {
  label: string;
  customContent: TemplateFieldDefinition[];
  globalContent: TemplateFieldDefinition[];
  settings: {
    enforceParent: boolean;
  };
  templateAssembly: AssemblySchema;
  /** Default field values to auto-populate when creating an entry with this template */
  defaultFields?: Record<string, string | string[]>;
};

export type StarterDefinition = {
  label: string;
  starterAssembly: AssemblySchema;
};

export const MergeField = z.enum([
  'CHILD_NAME',
  'UNAVAILABLE_CHILD_NAME',
  'GLOBAL_ID',
  'SOURCE_CODE',
  'CDSP_RATE',
  'COUNTRY_NAME',
]);
export type MergeField = z.infer<typeof MergeField>;

export type MergeFieldOptionsFallback = {
  cap?: string;
  default?: string;
};

export type MergeFieldOptions = {
  [key in MergeField]?: string | MergeFieldOptionsFallback;
};

export const countryContentIds = {
  intro: 'intro-content',
  facts: 'facts-content',
  map: 'map-location-content',
  keyChallenges: 'key-challenges-content',
  gallery: 'gallery-content',
  impactStats: 'our-impact-stats',
  impactCards: 'our-impact-cards',
  helpOverride: 'how-you-can-help-override',
  helpGlobal: 'how-you-can-help-global-content',
  faqOverride: 'faq-content-override',
  faq: 'faq-content',
} as const;

export const donationContentIds = {
  donationFund: 'donation-fund',
  stats: 'stats-content',
  howWeUseThis: 'how-we-use-this-content',
  finalDonateCTA: 'final-donate-cta-content',
  financialIntegrity: 'financial-integrity-content',
} as const;

export const memorialContentIds = {
  memorialDonateHuddle: 'memorial-donate-huddle',
  memorialExpandedContentShowcase: 'memorial-expanded-content-showcase',
  financialIntegrity: 'memorial-financial-integrity-content',
} as const;

export const giftCatalogContentIds = {
  donationFund: 'donation-fund',
  whatThisGiftProvides: 'what-this-gift-provides-content',
  faqOverride: 'faq-content-override',
  honorCard: 'honor-card-content',
  faq: 'faq-content',
} as const;

export const individualProfileContentIds = {
  profile: 'profile-content',
} as const;
